/**
 * Promesses Page Module
 * @module promessesPage
 * @description Contains page-specific functionality for promesses.html
 */

// Import dependencies
import { audioManager } from '../modules/audio-manager.js';

/**
 * Play the page load sound
 * @returns {Function} Cleanup function to stop the sound if needed
 */
function playPageLoadSound() {
     let soundCheckInterval;

     // Wait for de-l-argent sound to be loaded
     const checkSoundLoaded = () => {
          if (audioManager && audioManager.sounds.has('de-l-argent')) {
               audioManager.playSound('de-l-argent', {
                    volume: 1.0,
                    playbackRate: 1.0
               });
               clearInterval(soundCheckInterval);
          }
     };

     soundCheckInterval = setInterval(checkSoundLoaded, 100);

     // Return cleanup function
     return () => {
          clearInterval(soundCheckInterval);
          if (audioManager) {
               audioManager.stopSound('de-l-argent');
          }
     };
}

// Function to play promesse button sounds
function playPromesseSound(promesseId) {
     if (typeof audioManager === 'undefined') return;

     try {
          switch (promesseId) {
               case 1:
               case 2:
               case 3:
                    // Play merci for buttons 1, 2, and 3
                    if (audioManager.sounds.has('merci')) {
                         audioManager.playSound('merci', {
                              volume: 1.0,
                              playbackRate: 1.0
                         });
                    }
                    break;
               case 4:
                    // Play de-l-argent for button 4
                    if (audioManager.sounds.has('de-l-argent')) {
                         audioManager.playSound('de-l-argent', {
                              volume: 1.0,
                              playbackRate: 1.0
                         });
                    }
                    break;
               case 5:
                    // Play sirenes then merci for button 5
                    playSirenesMerciSequence();
                    break;
          }
     } catch (error) {
          console.error('Error playing promesse sound:', error);
     }
}

async function playSirenesMerciSequence() {
     try {
          // Play sirenes first
          if (audioManager.sounds.has('sirenes')) {
               audioManager.playSound('sirenes', {
                    volume: 1.0,
                    playbackRate: 1.0
               });

               // Wait for sirenes to finish (approximate duration)
               await new Promise(resolve => setTimeout(resolve, 2000));

               // Play merci
               if (audioManager.sounds.has('merci')) {
                    audioManager.playSound('merci', {
                         volume: 1.0,
                         playbackRate: 1.0
                    });
               }
          }
     } catch (error) {
          console.error('Error in sirenes-merci sequence:', error);
     }
}

/**
 * Tracks promesse clicks and manages theme switcher unlocking
 */
class PromesseTracker {
     constructor() {
          this.clickedPromesses = new Set();
          this.themeSwitcherUnlocked = false;
          this.init();
     }

     init() {
          // Load saved progress
          this.loadProgress();

          // Track all promesse buttons
          document.querySelectorAll('.promettre-btn').forEach((btn, index) => {
               const promesseId = index + 1;

               // Add click tracking - show clicked state on every click
               btn.addEventListener('click', () => {
                    // Play the appropriate sound for this promesse
                    playPromesseSound(promesseId);

                    // Show the clicked state every time
                    this.markButtonAsClicked(btn, promesseId, true); // true = show notification

                    // Only add to clicked set if not already clicked (for progress tracking)
                    if (!this.clickedPromesses.has(promesseId)) {
                         this.clickedPromesses.add(promesseId);
                         this.saveProgress();
                         this.checkThemeSwitcherUnlock();
                    }
               });
          });

          // Check if theme switcher should be unlocked
          this.checkThemeSwitcherUnlock();
     }

     markButtonAsClicked(button, promesseId, showNotification = true) {
          button.classList.add('clicked');
          button.innerHTML = '<span class="checkmark">✓</span> Promis';
          button.style.background = '#28a745';
          button.style.color = 'white';

          // Only show completion message if requested
          if (showNotification) {
               setTimeout(() => {
                    this.setupPopoverFadeOut(promesseId);
               }, 500);
          }
     }


     setupPopoverFadeOut(promesseId) {
          // Find the corresponding popover and button
          const popover = document.getElementById(`popover-${promesseId}`);
          const button = document.querySelector(`[popovertarget="popover-${promesseId}"]`);

          if (popover) {
               // Show the popover
               popover.showPopover();

               // Reset button when popover closes
               const resetButton = () => {
                    if (button) {
                         button.classList.remove('clicked');
                         button.innerHTML = 'Promettre';
                         button.style.background = '';
                         button.style.color = '';
                    }
               };

               // Listen for popover close events
               const handlePopoverToggle = (event) => {
                    if (!popover.matches(':popover-open')) {
                         resetButton();
                         popover.removeEventListener('toggle', handlePopoverToggle);
                    }
               };

               popover.addEventListener('toggle', handlePopoverToggle);

               // Auto-hide after 4 seconds
               setTimeout(() => {
                    if (popover.matches(':popover-open')) {
                         popover.hidePopover();
                    }
               }, 4000);
          }
     }

     loadProgress() {
          try {
               const saved = localStorage.getItem('promesseProgress');
               if (saved) {
                    const parsed = JSON.parse(saved);
                    this.clickedPromesses = new Set(parsed.clicked || []);
                    this.themeSwitcherUnlocked = parsed.themeSwitcherUnlocked || false;
               }
          } catch (error) {
               console.warn('Error loading promesse progress:', error);
          }
     }

     saveProgress() {
          try {
               const data = {
                    clicked: Array.from(this.clickedPromesses),
                    themeSwitcherUnlocked: this.themeSwitcherUnlocked
               };
               localStorage.setItem('promesseProgress', JSON.stringify(data));
          } catch (error) {
               console.warn('Error saving promesse progress:', error);
          }
     }

     checkThemeSwitcherUnlock() {
          // Unlock theme switcher after 3 promesses are clicked
          if (this.clickedPromesses.size >= 3 && !this.themeSwitcherUnlocked) {
               this.themeSwitcherUnlocked = true;
               this.saveProgress();

               // Show unlock message
               setTimeout(() => {
                    const message = document.createElement('div');
                    message.className = 'theme-unlock-message';
                    message.innerHTML = `
                    <div class="unlock-content">
                        <span class="unlock-icon">🎨</span>
                        <h3>Thèmes Débloqués !</h3>
                        <p>Vos promesses ont ouvert de nouveaux horizons créatifs.</p>
                        <p>Explorez les thèmes cachés dans le menu flottant.</p>
                    </div>
                `;

                    Object.assign(message.style, {
                         position: 'fixed',
                         top: '50%',
                         left: '50%',
                         transform: 'translate(-50%, -50%)',
                         background: 'var(--color-background)',
                         color: 'var(--color-text)',
                         padding: '2rem',
                         borderRadius: '12px',
                         boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
                         zIndex: '10001',
                         maxWidth: '400px',
                         textAlign: 'center',
                         animation: 'fadeInScale 0.6s ease-out'
                    });

                    document.body.appendChild(message);

                    // Unlock the floating theme switcher
                    if (typeof floatingThemeSwitcher !== 'undefined') {
                         floatingThemeSwitcher.unlock();
                    }

                    // Remove after 5 seconds
                    setTimeout(() => {
                         if (message.parentNode) {
                              message.style.animation = 'fadeOutScale 0.4s ease-in';
                              setTimeout(() => message.remove(), 400);
                         }
                    }, 5000);
               }, 1000);
          }
     }
}

// Module state
let promesseTracker;
let cleanupSound;

/**
 * Initialize the Promesses page functionality
 * @returns {Function} Cleanup function to remove event listeners and resources
 */
function initPromessesPage() {
     // Initialize page load sound
     cleanupSound = playPageLoadSound();

     // Initialize promesse tracker
     promesseTracker = new PromesseTracker();

     // Return cleanup function
     return function cleanup() {
          if (cleanupSound) cleanupSound();
          if (promesseTracker && typeof promesseTracker.cleanup === 'function') {
               promesseTracker.cleanup();
          }
     };
}

// Add cleanup method to PromesseTracker prototype
if (typeof PromesseTracker.prototype.cleanup !== 'function') {
     PromesseTracker.prototype.cleanup = function () {
          // Clean up any PromesseTracker resources here
          // This is a placeholder that can be implemented if needed
     };
}

// Auto-initialize if loaded directly in browser with type="module"
let cleanupPromessesPage;

function init() {
     cleanupPromessesPage = initPromessesPage();
}

// Initialize on DOMContentLoaded
if (document.readyState === 'loading') {
     document.addEventListener('DOMContentLoaded', init);
} else {
     init();
}

// Export public API
export {
     PromesseTracker,
     playPageLoadSound,
     playPromesseSound,
     playSirenesMerciSequence,
     initPromessesPage as init,
     cleanupPromessesPage as cleanup
};

export default {
     PromesseTracker,
     playPageLoadSound,
     playPromesseSound,
     playSirenesMerciSequence,
     init: initPromessesPage,
     cleanup: cleanupPromessesPage
};

// Add CSS animations for messages
const style = document.createElement('style');
style.textContent = `
    @keyframes slideIn {
        from { transform: translateX(100%); opacity: 0; }
        to { transform: translateX(0); opacity: 1; }
    }

    @keyframes slideOut {
        from { transform: translateX(0); opacity: 1; }
        to { transform: translateX(100%); opacity: 0; }
    }

    @keyframes fadeInScale {
        from { transform: translate(-50%, -50%) scale(0.8); opacity: 0; }
        to { transform: translate(-50%, -50%) scale(1); opacity: 1; }
    }

    @keyframes fadeOutScale {
        from { transform: translate(-50%, -50%) scale(1); opacity: 1; }
        to { transform: translate(-50%, -50%) scale(0.8); opacity: 0; }
    }
`;
document.head.appendChild(style);
